import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { Download, ChevronRight, ArrowLeft, ArrowRight } from "lucide-react";
import Layout from "../components/Layout";
import Seo from "../seo/Seo";
import { areaGraph } from "../seo/jsonld";
import { programAreas } from "../seo/config";
import { program as programMock } from "../mock";
import { chapters, getChapter } from "../programData";

const ProgramAreaPage = () => {
  const { slug } = useParams();
  const chapter = getChapter(slug);
  if (!chapter) return <Navigate to="/program" replace />;

  const idx = chapters.findIndex((c) => c.slug === slug);
  const prev = idx > 0 ? chapters[idx - 1] : null;
  const next = idx < chapters.length - 1 ? chapters[idx + 1] : null;
  const area = programAreas.find((a) => a.slug === slug);

  const description = `${chapter.title} – volebný program Jána Kuráňa pre Ružomberok 2026. ${chapter.tagline}`;
  const title = `${chapter.title} | Program Ján Kuráň Ružomberok 2026`;

  return (
    <Layout>
      <Seo
        title={title}
        description={description}
        path={`/program/${slug}`}
        jsonLd={areaGraph(area, { description })}
      />

      <section className="bg-campaign-blue px-4 sm:px-6 py-12 sm:py-16">
        <div className="max-w-3xl mx-auto">
          <nav aria-label="Omrvinková navigácia" className="text-campaign-cream/70 text-sm font-semibold mb-6 flex items-center gap-1 flex-wrap">
            <Link to="/" className="hover:text-campaign-yellow">Domov</Link>
            <ChevronRight size={14} />
            <Link to="/program" className="hover:text-campaign-yellow">Program</Link>
            <ChevronRight size={14} />
            <span className="text-campaign-yellow">{chapter.title}</span>
          </nav>
          <span className="font-display text-campaign-yellow text-2xl">{chapter.no}</span>
          <h1 className="font-display text-3xl sm:text-5xl text-campaign-cream leading-tight uppercase mt-1">
            {chapter.title}
          </h1>
          {chapter.tagline && (
            <p className="mt-4 font-semibold text-campaign-yellow text-lg">{chapter.tagline}</p>
          )}
        </div>
      </section>

      <section className="bg-campaign-cream px-4 sm:px-6 py-12 sm:py-16">
        <div className="max-w-3xl mx-auto">
          {chapter.intro && (
            <p className="text-campaign-ink/85 leading-relaxed mb-6 text-lg">{chapter.intro}</p>
          )}

          {chapter.sections.map((s, i) => (
            <div key={i} className="mt-6">
              <h2 className="font-display text-xl sm:text-2xl text-campaign-ink uppercase mb-3">{s.h}</h2>
              <ul className="space-y-2 list-disc pl-5">
                {s.items.map((it, j) => (
                  <li key={j} className="text-campaign-ink/85 leading-relaxed">{it}</li>
                ))}
              </ul>
            </div>
          ))}

          {chapter.benefits && chapter.benefits.length > 0 && (
            <div className="mt-8 bg-campaign-blue border-4 border-campaign-ink p-6">
              <h2 className="font-display text-campaign-yellow uppercase mb-3">Čo tým získa obyvateľ?</h2>
              <ul className="space-y-2 list-disc pl-5">
                {chapter.benefits.map((b, k) => (
                  <li key={k} className="text-campaign-cream/90 leading-relaxed">{b}</li>
                ))}
              </ul>
            </div>
          )}

          <a
            href={programMock.pdfUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-8 inline-flex items-center gap-2 bg-campaign-yellow text-campaign-ink font-display uppercase text-sm px-5 py-3 border-2 border-campaign-ink shadow-brutal hover-lift"
          >
            <Download size={18} /> Celý program v PDF
          </a>

          {/* Navigácia medzi oblasťami */}
          <div className="mt-10 flex justify-between gap-4 border-t-2 border-campaign-ink/15 pt-6">
            {prev ? (
              <Link to={`/program/${prev.slug}`} className="inline-flex items-center gap-1 font-semibold text-campaign-blue hover:underline max-w-[45%]">
                <ArrowLeft size={16} /> {prev.title}
              </Link>
            ) : <span />}
            {next ? (
              <Link to={`/program/${next.slug}`} className="inline-flex items-center gap-1 font-semibold text-campaign-blue hover:underline text-right max-w-[45%]">
                {next.title} <ArrowRight size={16} />
              </Link>
            ) : <span />}
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default ProgramAreaPage;
